import { Button, Loader } from '@mantine/core'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { parsePatientChartSplat } from '../patientChartPath'
import { useMedplum } from '@medplum/react'
import { formatHumanName, type MedplumClient } from '@medplum/core'
import {
  Claim,
  ClaimItem,
  Condition,
  DocumentReference,
  Encounter,
  Practitioner,
  Provenance,
  Reference,
  Resource,
} from '@medplum/fhirtypes'
import { v4 } from 'uuid'
import { showNotification } from '@mantine/notifications'
import { parseCCDADocument } from '@/utils/CCDAParser.ts'
import { formatDateTime, formatSignatureLine } from '@/utils'
import { getPractitionerDisplayName } from '@/utils/providerUtils'
import { tiffToURL } from '@/utils/tiffToURL.ts'
import { ccdaToPdf } from '@/utils/textToPdf.ts'
import { progressNoteToPdf, type PdfAddendum } from '@/utils/progressNotePdf'
import { ADDENDUM_REASON_LABELS, fetchNoteAddenda } from '@/pages/Patient/hooks/useNoteAddenda'
import { PdfViewer } from '@/components/PdfViewer'
import { IconDownload } from '@tabler/icons-react'
import { ProgressNoteEdit } from '@/pages/Patient/components/ProgressNoteEdit'
import { ProgressNoteViewer } from '@/pages/Patient/components/ProgressNoteViewer'
import { ElectronicSignature } from '@/pages/Patient/components/ProgressNoteViewer/ElectronicSignature'
import { toFormBillingSuggestion } from '@/components/billing'
import { useOrpc } from '@/hooks/useClient'
import { useIsManager } from '@/hooks/useIsManager'
import {
  isScribeBackedProvenance,
  isUnreviewedAiDraft,
  resolveNoteRenderMode,
} from '@/pages/Patient/components/ProgressNoteViewer/scribeDetection'
import { readScribeDraftPayload } from '@/pages/Patient/components/ProgressNoteEdit/scribeDraftPayload'
import { CareCoordinatorNote } from '@/pages/Patient/components/CareCoordinatorNote'
import { ReviewFollowupModal } from '@/pages/Patient/components/ReviewFollowupModal'
import { usePatientSidePanel } from '@/pages/Patient/components/PatientSidePanelContext'
import { useScrollContext } from '@/context/ScrollContext'
import { logError } from '@/utils/errorUtils'
import { resolveEncounterClaim } from '@/utils/encounter-claim'
import {
  getProvider,
  getProviderReference,
  getSource,
  getSourceReference,
  resolveDocumentProvenance,
  resolveNoteSignature,
  type NoteSignature,
} from '@/utils/provenance'
import { downloadAttachment } from '@/utils/downloadAttachment'
import { useMedplumPatient } from '@/hooks/useMedplumPatient'
import {
  useAuditDocumentOpenReporting,
  useAuditDownloadReporting,
  useAuditExportReporting,
} from '@/pages/Patient/useAuditDocumentReporting'
import { usePatient } from '@/hooks/usePatient'
import { useFollowupStatus } from '@/hooks/useFollowupStatus'
import { patientChartCountsQueryKey } from '@/hooks/patientChartCountsQueryKey'
import { NoteEditorContext } from './progressNoteRemoval'

type CCDATable = {
  title: string
  textContent: string
  textElement: Element
}

type Props = {
  title: string
  sourceResourceId?: string
}

type Attachment = {
  title: string
  url: string
}

function formatCareCoordinatorDisplayDate(value: string): string {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) {
    return value
  }

  return `${match[2]}/${match[3]}/${match[1]}`
}

/**
 * Pull the `Encounter/...` reference off a Progress Note Provenance. Progress
 * Notes link to their encounter via `Provenance.entity[].what.reference`;
 * this mirrors the helper in `ProgressNoteEdit.tsx` and is used to drive both
 * `<ProgressNoteViewer>` (Stats Strip + Lock action) and our own encounter
 * snapshot fetch.
 */
function getEncounterRefFromProvenance(provenance: Provenance | undefined): string | undefined {
  return provenance?.entity
    ?.map((entity) => entity.what?.reference)
    .find((reference): reference is string => Boolean(reference?.startsWith('Encounter/')))
}

function normalizeCareCoordinatorFollowUpDateTables(tables: CCDATable[]): CCDATable[] {
  for (const table of tables) {
    if (table.title !== 'Outcome and follow-up') {
      continue
    }

    let didFormat = false
    table.textElement.querySelectorAll('tr').forEach((row) => {
      const cells = row.querySelectorAll('th, td')
      if (cells[0]?.textContent?.trim() !== 'Follow-up date' || !cells[1]) {
        return
      }

      const currentValue = cells[1].textContent?.trim() ?? ''
      const formattedValue = formatCareCoordinatorDisplayDate(currentValue)
      if (formattedValue !== currentValue) {
        cells[1].textContent = formattedValue
        didFormat = true
      }
    })

    if (didFormat) {
      table.textContent = table.textElement.innerHTML
    }
  }

  return tables
}

/** "Name, MD" from the signer's profile, as the viewer shows it (PD-1897). */
async function readSignerName(
  medplum: MedplumClient,
  signer: Reference<Practitioner> | undefined,
  fallback: string,
): Promise<string> {
  if (!signer) return fallback
  try {
    return getPractitionerDisplayName(await medplum.readReference(signer))
  } catch (err) {
    logError('patient', 'Read the signer for the Progress Note PDF', err)
    return fallback
  }
}

/**
 * Signed addenda for the PDF (AC#9). Drafts are excluded — they are not part of
 * the record. A failure must not block the export, so it degrades to none.
 */
async function fetchSignedAddendaForPdf(
  medplum: MedplumClient,
  documentReferenceId: string | undefined,
): Promise<PdfAddendum[]> {
  if (!documentReferenceId) return []
  try {
    const addenda = await fetchNoteAddenda(medplum, documentReferenceId)
    return await Promise.all(
      addenda
        .filter((addendum) => addendum.signed)
        .map(async (addendum) => {
          const signerName = await readSignerName(medplum, addendum.signer, addendum.author)
          return {
            reason: ADDENDUM_REASON_LABELS[addendum.reason],
            signature: addendum.date
              ? formatSignatureLine(signerName, addendum.date)
              : `Electronically signed by ${signerName}.`,
            content: addendum.content,
          }
        }),
    )
  } catch (err) {
    logError('patient', 'Load addenda for the Progress Note PDF', err)
    return []
  }
}

export const Documents = ({ title, sourceResourceId }: Props) => {
  const params = useParams()
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const orpc = useOrpc()
  const { scrollToTop, scrollContainerId } = useScrollContext()
  const { emitPanelEvent } = usePatientSidePanel()

  const [resourceId, setResourceId] = useState<string | undefined>(sourceResourceId)
  const [resource, setResource] = useState<DocumentReference>()
  const [provenance, setProvenance] = useState<Provenance>()
  const [noteSignature, setNoteSignature] = useState<NoteSignature>()
  // The lookup in flight, so an export started before it settles still signs the PDF.
  const pendingNoteSignature = useRef<Promise<NoteSignature | undefined>>(undefined)
  const [claim, setClaim] = useState<Claim>()
  const [encounter, setEncounter] = useState<Encounter>()
  const [diagnosisBySequence, setDiagnosisBySequence] = useState<Record<string, string>>({})
  const [isNotFound, setIsNotFound] = useState(false)
  const [ccdaTables, setCcdaTables] = useState<CCDATable[]>([])
  const [images, setImages] = useState<string[]>([])
  const [tifImages, setTifImages] = useState<string[]>([])
  const [pdfs, setPdfs] = useState<Attachment[]>([])
  const [textHtml, setTextHtml] = useState<string[]>([])
  const [restAttachmentUrls, setRestAttachmentUrls] = useState<Attachment[]>([])
  const [isExporting, setIsExporting] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  // PD-2031 — the chart rail does not offer to remove the note open in the editor.
  const reportNoteInEditor = useContext(NoteEditorContext)
  const noteInEditor = showEdit ? resource?.id : undefined
  useEffect(() => {
    reportNoteInEditor(noteInEditor)
    return () => reportNoteInEditor(undefined)
  }, [reportNoteInEditor, noteInEditor])
  // PD-1330 — first open of an auto-created draft applies the stored AI draft.
  const [autoApplyAiDraft, setAutoApplyAiDraft] = useState(false)
  // Managers (care coordinators) can't create/edit the scribe progress note.
  // Fail-open (false while loading).
  const { isManager } = useIsManager()
  const [componentKey, setComponentKey] = useState<string>(v4())
  const [isFollowupModalOpen, setIsFollowupModalOpen] = useState(false)
  // Followup-instruction send-status (main feature `useFollowupStatus`): looks
  // up the `PatientInstructions` DocumentReference that `relatesto` this note
  // so the modal can show an "already sent" state and refresh after a save.
  const {
    isFollowupSaved,
    isFollowupLoading,
    invalidate: invalidateFollowupStatus,
  } = useFollowupStatus(resource?.id)
  // Portal target for the Progress Note action group (Review Followup /
  // Export to PDF / Edit / Lock Note). The slot lives in the title row;
  // `<ProgressNoteViewer>` renders its buttons into this element via
  // `createPortal` so the mockup's "title + actions on same row" layout
  // works without lifting lock-modal state out of the viewer.
  const [headerActionsEl, setHeaderActionsEl] = useState<HTMLDivElement | null>(null)
  // Portal target for the locked-note banner ("Note Locked / TEFCA").
  // The slot sits ABOVE the title row per mockup
  // `ui-designs/.../ChartPage.tsx:3919-3929`; the viewer portals the banner
  // into it when the doc is `docStatus=final`.
  const [lockedBannerEl, setLockedBannerEl] = useState<HTMLDivElement | null>(null)

  const patientId = resource?.subject?.reference?.split('/')[1]
  const { data: patient } = useMedplumPatient(patientId)

  // PD-1322 (S14) — audit reporting for document opens/downloads. The open
  // fires once per document per mount after the loaded document survives the
  // debounce; downloads report from the choke points below (Export to PDF,
  // attachment links). Server authors the actual audit records.
  useAuditDocumentOpenReporting(patientId, resource?.id)
  const reportAuditDownload = useAuditDownloadReporting(patientId)
  // QA round 1 (M3) — the Export to PDF control reports document-export, not
  // document-download: the record says what the user-facing control said.
  const reportAuditExport = useAuditExportReporting(patientId)
  // Same source `SubjectiveSection` uses for the Consent sentence — the PDF
  // export must render the identical consent text as the viewer.
  const identityVerified = usePatient(patientId).data?.identityVerified ?? false

  // PD-1303 — saving patient instructions writes a `PatientInstructions`
  // DocumentReference, which belongs to the chart's "Documents" section. That
  // section's count badge is a TanStack query (`staleTime: 5 min`, seeded from
  // localStorage), so without an explicit invalidation it kept showing the
  // pre-save number until the page was reloaded. Also bust the Medplum SDK
  // search cache (`LayoutContext.tsx:58` sets `cacheTime: 5000`) so the
  // refetched count query can't read a stale pre-save Bundle. Prior art:
  // `useLockAndTransmit.ts:74-84`.
  const handleFollowupSaved = () => {
    void invalidateFollowupStatus()
    medplum.invalidateSearches('DocumentReference')
    if (patientId) {
      void queryClient.invalidateQueries({ queryKey: patientChartCountsQueryKey(patientId) })
    }
  }

  // A scribe-draft blob exists only on a scribe-written Progress Note; its
  // presence both gates the Export button and routes export to the structured
  // PDF generator (PD-1153). Parsed once per resource, not per render.
  const scribeDraft = useMemo(
    () => (resource ? readScribeDraftPayload(resource) : undefined),
    [resource],
  )

  const panelRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => {
    if (sourceResourceId) {
      setResourceId(sourceResourceId)
      return
    }
    const parsedResourceId = parsePatientChartSplat(params['*']).resourceId

    if (parsedResourceId !== resourceId) {
      setResourceId(parsedResourceId)
    }
  }, [params, resourceId, sourceResourceId])

  useEffect(() => {
    scrollToTop()
  }, [resourceId, scrollToTop])

  // Switching documents always lands in VIEW mode — `showEdit` would otherwise
  // survive the swap and open the next note straight in the editor.
  useEffect(() => {
    setShowEdit(false)
    setAutoApplyAiDraft(false)
  }, [resourceId])

  // PD-1330 — an auto-created, never-reviewed AI draft skips the viewer: the
  // editor shows the Review-and-Sign disclaimer and applies the stored draft
  // (whose forced flush clears the unreviewed tag server-side).
  useEffect(() => {
    if (!resource || isManager) return
    if (
      isProgressNote(resource) &&
      // A draft removed from the chart (PD-2031) is not opened for editing again.
      resource.status !== 'entered-in-error' &&
      isUnreviewedAiDraft(resource) &&
      hasQuestionnaireResponseTarget(provenance)
    ) {
      setShowEdit(true)
      setAutoApplyAiDraft(true)
    }
  }, [resource, provenance, isManager])

  useEffect(() => {
    let active = true
    if (resourceId) {
      medplum
        .search('DocumentReference', `_id=${resourceId}&_revinclude=Provenance:target`)
        .then((resources) => {
          const neededResource = resources.entry?.find(
            (resource) => resource.resource?.id === resourceId,
          )?.resource as DocumentReference | undefined

          const provenances = (resources.entry ?? [])
            .map((entry) => entry.resource as Resource | undefined)
            .filter((r): r is Provenance => r?.resourceType === 'Provenance')

          const resolvedProvenance = resolveDocumentProvenance(
            provenances,
            `DocumentReference/${resourceId}`,
          )
          setProvenance(resolvedProvenance)
          if (neededResource) {
            setResource(neededResource)
            setIsNotFound(false)
            // PD-1897 — only our own Progress Notes carry our signature; imports and other
            // documents do not, nor Care Coordinator notes, which are edited in place with no lock.
            const renderMode = resolveNoteRenderMode(neededResource, resolvedProvenance)
            if (
              (renderMode === 'structured-readonly' || renderMode === 'document-fallback') &&
              !isCareCoordinatorNote(neededResource)
            ) {
              const pending = resolveNoteSignature(medplum, neededResource, provenances)
              pendingNoteSignature.current = pending
              pending
                .then((signature) => {
                  if (active) setNoteSignature(signature)
                })
                .catch((err) => logError('patient', 'Resolve the Progress Note signature', err))
            }
          }
        })
        .catch((e) => {
          setIsNotFound(true)
          logError('patient', 'Fetch document reference', e)
          showNotification({
            title: 'Error',
            message: 'Failed to fetch resource',
            color: 'red',
          })
        })
    }

    return () => {
      active = false
      setResource(undefined)
      setProvenance(undefined)
      setNoteSignature(undefined)
      pendingNoteSignature.current = undefined
      setIsNotFound(false)
    }
  }, [medplum, resourceId, componentKey])

  useEffect(() => {
    if (!provenance) {
      setClaim(undefined)
      setDiagnosisBySequence({})
      return
    }

    // Resolve through the shared rule rather than picking the first `Claim/`
    // reference out of the unioned Provenance targets. That pick had no ordering,
    // so on an encounter with more than one Claim this surface could land on a
    // different one than the Billing page — the same visit, two sets of codes.
    const claimEncounterId = getEncounterRefFromProvenance(provenance)?.split('/')[1]

    if (!claimEncounterId) {
      setClaim(undefined)
      setDiagnosisBySequence({})
      return
    }

    resolveEncounterClaim(medplum, claimEncounterId)
      .then(async (claimResource) => {
        if (!claimResource) {
          setClaim(undefined)
          setDiagnosisBySequence({})
          return
        }
        setClaim(claimResource)

        const diagnosisRefs =
          claimResource.diagnosis
            ?.map((diagnosis) => diagnosis.diagnosisReference?.reference)
            .filter(Boolean) || []
        let conditionsById = new Map<string, Condition>()

        if (diagnosisRefs.length) {
          const diagnosisIds = diagnosisRefs
            .map((reference) => reference?.split('/')[1])
            .filter(Boolean)
            .join(',')

          if (diagnosisIds) {
            const conditions = await medplum.search('Condition', `_id=${diagnosisIds}`)
            const conditionResources = (conditions.entry?.map((entry) => entry.resource) ||
              []) as Condition[]
            conditionsById = new Map(
              conditionResources.filter((condition) => condition?.id).map((c) => [c.id!, c]),
            )
          }
        }

        const diagnosisMap: Record<string, string> = {}
        for (const diagnosis of claimResource.diagnosis || []) {
          const sequence = diagnosis.sequence?.toString()
          if (!sequence) continue

          const ref = diagnosis.diagnosisReference?.reference
          const conditionId = ref?.split('/')[1]
          const condition = conditionId ? conditionsById.get(conditionId) : undefined
          const coding = condition?.code?.coding?.[0]
          const code =
            coding?.code ||
            coding?.display ||
            condition?.code?.text ||
            diagnosis.diagnosisCodeableConcept?.coding?.[0]?.code ||
            diagnosis.diagnosisCodeableConcept?.coding?.[0]?.display ||
            diagnosis.diagnosisCodeableConcept?.text ||
            ''

          if (code) {
            diagnosisMap[sequence] = code
          }
        }

        setDiagnosisBySequence(diagnosisMap)
      })
      .catch((e) => {
        logError('patient', 'Fetch claim', e)
        setClaim(undefined)
        setDiagnosisBySequence({})
      })
  }, [medplum, provenance, componentKey])

  // Resolve the Progress Note's Encounter snapshot so <ProgressNoteViewer>
  // can drive the Stats Strip (Session Duration card + encounter-scoped FHIR
  // count queries) and the lock action. Older / non-scribe Progress Notes may
  // not carry an Encounter reference on their Provenance — the viewer is
  // null-safe and renders without the strip in that case.
  useEffect(() => {
    const encounterRef = getEncounterRefFromProvenance(provenance)
    const encounterId = encounterRef?.split('/')[1]

    if (!encounterId) {
      setEncounter(undefined)
      return
    }

    let cancelled = false
    medplum
      .readResource('Encounter', encounterId)
      .then((enc) => {
        if (!cancelled) setEncounter(enc)
      })
      .catch((e) => {
        logError('patient', 'Fetch encounter for progress note', e)
        if (!cancelled) setEncounter(undefined)
      })

    return () => {
      cancelled = true
    }
  }, [medplum, provenance, componentKey])

  const encounterId = useMemo(
    () => getEncounterRefFromProvenance(provenance)?.split('/')[1],
    [provenance],
  )

  // The note's billing projection for the PDF export — the same read the on-screen
  // sections and the Billing page render, so the export can never print a different
  // code than the note above it.
  const visitBilling = useQuery(
    orpc.visitBillingGet.queryOptions({
      input: { encounterId: encounterId ?? '' },
      enabled: Boolean(encounterId),
    }),
  )
  const billingSuggestion = useMemo(
    () => toFormBillingSuggestion(visitBilling.data?.billingSuggestion),
    [visitBilling.data],
  )

  /**
   * When the document is a locked Progress Note, `<ProgressNoteViewer>` renders
   * the 5 structured sections (Encounter Details / Subjective / Objective /
   * Assessment / Plan) inline above this area. The legacy CCDA panels below
   * would duplicate the same data using a different section list (e.g. "Reason
   * for Visit") and reintroduce the very mismatch this view is correcting, so
   * we hide both the CCDA TOC and the CCDA tables in that case.
   *
   * LD-21: previously gated on `isScribeBackedProvenance(provenance)` (strict
   * `Device/scribe-ai-v1` match). Legacy notes without the scribe device
   * silently fell through to the CCDA render with the wrong section list.
   *
   * Never-blank fix: the gate is now driven by `resolveNoteRenderMode`. CCDA
   * tables are hidden ONLY when the viewer actually renders the structured
   * 5-section layout (`structured-editable` / `structured-readonly`). For
   * `external-document` (Health Gorilla import) and `document-fallback` (our
   * Progress Note with no QR / draft blob) the CCDA tables MUST show — that is
   * the original-document render that keeps legacy / imported notes from
   * appearing blank. `ccda-document` (non-Progress-Note) is unchanged.
   */
  const noteRenderMode = useMemo(
    () => resolveNoteRenderMode(resource, provenance),
    [resource, provenance],
  )
  const hideCcdaTables =
    noteRenderMode === 'structured-editable' || noteRenderMode === 'structured-readonly'

  useEffect(() => {
    if (resource) {
      for (const content of resource.content || []) {
        if (content.attachment && content.attachment?.url) {
          if (content.attachment.contentType?.match(/(xml)/)) {
            downloadAttachment(medplum, content.attachment.url)
              .then((blob) => blob.text())
              .then((text) => {
                const tables = parseCCDADocument(text)
                setCcdaTables(
                  isCareCoordinatorNote(resource)
                    ? normalizeCareCoordinatorFollowUpDateTables(tables)
                    : tables,
                )
              })
              .catch((err) => {
                logError('patient', 'Fetch CCDA content', err)
              })

            continue
          }

          if (
            content.attachment.contentType?.match(/(imag)/) &&
            !content.attachment.contentType?.match(/(tiff)/)
          ) {
            setImages((prev) => [...prev, content.attachment.url!])

            continue
          }

          if (content.attachment.contentType?.match(/(pdf)/)) {
            setPdfs((prev) => [
              ...prev,
              {
                url: content.attachment.url!,
                title: content.attachment.title || '',
              },
            ])

            continue
          }

          if (content.attachment.contentType?.match(/(text)/)) {
            downloadAttachment(medplum, content.attachment.url)
              .then((blob) => blob.text())
              .then((text) => {
                setTextHtml((prev) => [...prev, text])
              })
              .catch((err) => {
                logError('patient', 'Fetch text content', err)
              })

            continue
          }

          if (content.attachment.contentType?.match(/(tiff)/)) {
            void tiffToURL(medplum, content.attachment.url).then((res) => {
              setTifImages((prev) => [...prev, ...res])
            })
            continue
          }

          setRestAttachmentUrls((prev) => [
            ...prev,
            {
              url: content.attachment.url || '',
              title: content.attachment.title || '',
            },
          ])
        }
      }
    }

    return () => {
      setCcdaTables([])
      setImages([])
      setTifImages([])
      setPdfs([])
      setTextHtml([])
      setRestAttachmentUrls([])
    }
  }, [medplum, resource])

  function scrollToPanel(id: string) {
    const element = panelRefs.current[id]
    if (!element) return

    const yOffset = -150

    if (scrollContainerId) {
      const container = document.getElementById(scrollContainerId)
      if (container) {
        const containerRect = container.getBoundingClientRect()
        const elementRect = element.getBoundingClientRect()
        const top = elementRect.top - containerRect.top + container.scrollTop + yOffset

        container.scrollTo({ top, behavior: 'smooth' })
        return
      }
    }

    // Without a scroll-context id the scrolling element is the chart pane's
    // inner overflow container, not the window — scrollIntoView finds it.
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function getType(docRef: DocumentReference) {
    return docRef.type?.text || docRef.type?.coding?.[0]?.display || '-'
  }

  function isProgressNote(docRef: DocumentReference) {
    const typeText = docRef.type?.text?.toLowerCase() || ''
    const codeMatch =
      docRef.type?.coding?.some(
        (coding) =>
          coding.code === 'ProgressNote' || coding.display?.toLowerCase().includes('progress note'),
      ) || false
    return codeMatch || typeText.includes('progress note')
  }

  function isCareCoordinatorNote(docRef: DocumentReference) {
    return (
      docRef.category?.some((cat) => cat.coding?.some((c) => c.code === 'care-coordinator-note')) ??
      false
    )
  }

  function hasQuestionnaireResponseTarget(prov?: Provenance) {
    return Boolean(
      prov?.target?.some((target) => target.reference?.startsWith('QuestionnaireResponse/')),
    )
  }

  function getProcedureCode(item?: ClaimItem) {
    return item?.productOrService?.coding?.[0]?.code || item?.productOrService?.text || '-'
  }

  function getModifiers(item?: ClaimItem) {
    const modifiers =
      item?.modifier
        ?.map((modifier) => modifier.coding?.[0]?.code || modifier.text || '')
        .filter(Boolean) || []
    return modifiers.length ? modifiers.join('-') : ''
  }

  function getProcedureDisplay(item?: ClaimItem) {
    const code = getProcedureCode(item)
    const modifiers = getModifiers(item)
    if (!modifiers || code === '-') {
      return code
    }
    return `${code} (${modifiers})`
  }

  function getDiagnosisCodes(item?: ClaimItem) {
    const sequences = item?.diagnosisSequence || []
    const codes = sequences
      .map((sequence) => diagnosisBySequence[sequence.toString()])
      .filter(Boolean)
    return codes.length ? codes.join(', ') : '-'
  }

  function formatMoney(value?: number) {
    if (value === undefined) return '-'
    return value.toFixed(2)
  }

  async function exportToPDF() {
    // Bound before any await: opening another note mid-export repoints the ref.
    const signatureLookup = noteSignature
      ? Promise.resolve(noteSignature)
      : pendingNoteSignature.current
    setIsExporting(true)
    try {
      // `patient` rides its own non-retrying query; read it on demand so a slow
      // or failed load can't export a note with placeholder identity.
      const notePatient =
        patient ?? (patientId ? await medplum.readResource('Patient', patientId) : undefined)
      const patientName = notePatient?.name?.[0]
        ? formatHumanName(notePatient.name[0])
        : (resource?.subject?.display ?? '—')
      // skipTimezone: `birthDate` is a bare date; converting shifts it a day back.
      const patientDob = notePatient?.birthDate
        ? formatDateTime(notePatient.birthDate, '-', false, true)
        : '—'

      const signed = await signatureLookup?.catch(() => undefined)
      const signature = signed
        ? formatSignatureLine(
            await readSignerName(medplum, signed.signer, signed.signer.display ?? '—'),
            signed.signedAt,
          )
        : undefined

      // Blob-backed Progress Notes export via the structured generator that
      // mirrors the viewer; external/legacy/QR-only docs keep the CCDA path.
      if (scribeDraft && resource) {
        await progressNoteToPdf({
          metadata: {
            title,
            source: getSource(provenance),
            type: getType(resource),
            documentName: resource.description || resource.type?.text || '-',
            provider: getProvider(provenance),
            encounterDate: encounter?.period?.start
              ? formatDateTime(encounter.period.start, '-', true)
              : '—',
            addedOn: resource.date ? formatDateTime(resource.date, '-', true) : '-',
            modifiedOn: resource.meta?.lastUpdated
              ? formatDateTime(resource.meta.lastUpdated, '-', true)
              : '-',
            patientName,
            patientDob,
          },
          documentReference: resource,
          soapNote: scribeDraft.soapNote,
          identityVerified,
          signature,
          addenda: await fetchSignedAddendaForPdf(medplum, resource.id),
          // Level of service + face-to-face minutes come from the projection, not
          // the draft blob — no billing edit ever rewrites the blob, so the blob's
          // copy goes stale the moment anyone re-codes the visit.
          billingSuggestion,
        })
      } else {
        await ccdaToPdf(
          {
            title,
            source: getSource(provenance),
            type: getType(resource!),
            documentName: resource?.description || resource?.type?.text || '-',
            provider: getProvider(provenance),
            fetchedOn: resource?.date ? formatDateTime(resource.date, '-', true) : '-',
            modifiedOn: resource?.meta?.lastUpdated
              ? formatDateTime(resource.meta.lastUpdated, '-', true)
              : '-',
            patientName,
            patientDob,
          },
          ccdaTables,
          textHtml,
          signature,
        )
      }

      // S14/M3 — the PDF export leaves the building; report it for the audit
      // trail under the EXPORT action (once per document per chart visit,
      // server-authored record with the document's own title).
      if (resource?.id) {
        reportAuditExport(resource.id)
      }

      showNotification({
        title: 'Success',
        message: 'PDF exported successfully',
        color: 'green',
      })
    } catch (error) {
      logError('patient', 'Export PDF', error)
      showNotification({
        title: 'Error',
        message: 'Failed to export PDF',
        color: 'red',
      })
    } finally {
      setIsExporting(false)
    }
  }

  if (!resourceId || isNotFound) {
    return null
  }

  if (!resource) {
    return (
      <div
        style={{ width: 'calc(100% - 560px - var(--zoom-panel-width, 0px))' }}
        className="fixed left-[560px] flex h-full items-center justify-center gap-3 text-sm text-[#7D7987]"
      >
        <Loader size="sm" />
        Loading document...
      </div>
    )
  }

  return (
    <div className="relative box-border p-2 whitespace-pre-wrap">
      {/*<div className='whitespace-pre-wrap'>{JSON.stringify(resource, null, 2)}</div>*/}
      <div className="w-full bg-white pt-2">
        {/* Locked banner slot — ProgressNoteViewer portals the navy "Note Locked"
            ribbon into this div when docStatus=final. Sits ABOVE the title row
            per mockup `ui-designs/.../ChartPage.tsx:3919-3929`. */}
        {isProgressNote(resource) && <div ref={setLockedBannerEl} className="mb-4" />}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold">{title}</p>
            {/* AI / Draft pills next to the Progress Note title. Mirror the
                left-rail pills in `ListLayout.tsx:271-317` so doctors get the
                same at-a-glance signal whether they're scanning the list or
                already inside the open document. */}
            {isProgressNote(resource) && isScribeBackedProvenance(provenance) && (
              <span
                data-testid="progress-note-title-ai-pill"
                style={{
                  display: 'inline-block',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 7px',
                  borderRadius: 999,
                  background: 'rgba(99, 102, 241, 0.12)',
                  color: '#6366f1',
                }}
              >
                AI
              </span>
            )}
            {isProgressNote(resource) && resource.docStatus === 'preliminary' && (
              <span
                data-testid="progress-note-title-draft-pill"
                style={{
                  display: 'inline-block',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 7px',
                  borderRadius: 999,
                  background: 'rgba(217, 119, 6, 0.12)',
                  color: '#b45309',
                }}
              >
                Draft
              </span>
            )}
          </div>
          {/*
           * LD-21 — Action buttons (Review Followup / Export to PDF / Edit
           * / Lock Note) for Progress Notes are owned by `<ProgressNoteViewer>`
           * below (lock modal state lives there). To match the mockup
           * (`ui-designs/.../ChartPage.tsx:3951-3987`) which places the
           * buttons on the SAME ROW as the title, we provide an empty target
           * `<div>` here and pass its ref to the viewer via
           * `actionGroupTarget` — the viewer portals its action group into
           * this slot at render time.
           *
           * For non-Progress-Note documents (care-coordinator notes, raw
           * CCDA uploads, …) we keep the legacy buttons here.
           */}
          {isProgressNote(resource) ? (
            <div className="flex gap-2" ref={setHeaderActionsEl} />
          ) : (
            <div className="flex gap-2">
              {(!!ccdaTables.length || !!textHtml.length) && (
                <Button
                  onClick={exportToPDF}
                  loading={isExporting}
                  leftSection={<IconDownload size={16} />}
                  variant="light"
                >
                  Export to PDF
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Legacy metadata block (Source / Type / Document Name / Provider /
            Added / Modified). Hidden for structured Progress Notes — those
            render the same data via `<NoteMetadataGrid>` mounted inside
            `<ProgressNoteViewer>` below to match the mockup. Non-Progress-Note
            documents still see this block. */}
        {!hideCcdaTables && (
          <div className="flex flex-wrap gap-x-16 gap-y-4 whitespace-normal">
            <dl className="grid max-w-[560px] min-w-[300px] grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2">
              <dt className="text-sm font-bold whitespace-nowrap text-[#7D7987]">Source:</dt>
              <dd className="text-sm break-words">{getSource(provenance)}</dd>
              <dt className="text-sm font-bold whitespace-nowrap text-[#7D7987]">Type:</dt>
              <dd className="text-sm break-words">{getType(resource)}</dd>
              <dt className="text-sm font-bold whitespace-nowrap text-[#7D7987]">Document Name:</dt>
              <dd className="text-sm break-words">
                {resource.description || resource.type?.text || '-'}
              </dd>
              <dt className="text-sm font-bold whitespace-nowrap text-[#7D7987]">Provider:</dt>
              <dd className="text-sm break-words">{getProvider(provenance)}</dd>
            </dl>

            <dl className="grid min-w-[260px] shrink-0 grid-cols-[max-content_max-content] gap-x-4 gap-y-2">
              <dt className="text-sm font-bold whitespace-nowrap text-[#7D7987]">Added on:</dt>
              <dd className="text-sm whitespace-nowrap">
                {resource.date ? formatDateTime(resource.date, '-', true) : '-'}
              </dd>
              <dt className="text-sm font-bold whitespace-nowrap text-[#7D7987]">Modified on:</dt>
              <dd className="text-sm whitespace-nowrap">
                {resource.meta?.lastUpdated
                  ? formatDateTime(resource.meta.lastUpdated, '-', true)
                  : '-'}
              </dd>
            </dl>
          </div>
        )}

        {/*
         * Phase 6.5 + LD-21 — Progress Note chrome (locked banner + top
         * action group + Stats Strip + TOC + Billing Summary). Mounted
         * ABOVE the existing CCDA tables so the locked-note read view
         * shows the new chrome first while keeping the canonical document
         * render below untouched (Option A — non-destructive integration).
         *
         * The viewer hosts the action buttons (Review Followup / Export to
         * PDF / Edit / Lock Note) per mockup `ChartPage.tsx:3950-3987`.
         * Documents.tsx remains the owner of:
         *   - the `<ReviewFollowupModal>` render + open state
         *   - the `<ProgressNoteEdit>` panel + `showEdit` toggle
         *   - the CCDA-driven `exportToPDF()` call
         * The viewer is purely presentational for those — it just renders
         * the triggers and forwards clicks via callback props.
         */}
        {isProgressNote(resource) && !showEdit && (
          <div className="mt-6">
            <ProgressNoteViewer
              documentReference={resource}
              renderMode={noteRenderMode}
              encounterId={encounterId}
              encounter={encounter}
              provenance={provenance}
              signature={noteSignature}
              showReviewFollowup={true}
              onReviewFollowup={() => setIsFollowupModalOpen(true)}
              followupSaved={isFollowupSaved}
              followupLoading={isFollowupLoading}
              // Blob-backed notes export without waiting on the CCDA parse
              // (PD-1153 structured path); others still need parsed content.
              showExportToPdf={!!scribeDraft || !!ccdaTables.length || !!textHtml.length}
              onExportToPdf={exportToPDF}
              isExporting={isExporting}
              canEdit={hasQuestionnaireResponseTarget(provenance) && !isManager}
              isEditing={false}
              onToggleEdit={() => setShowEdit(true)}
              actionGroupTarget={headerActionsEl}
              lockedBannerTarget={lockedBannerEl}
              // Bump the cache-busting `componentKey` after a successful lock
              // so the `useEffect` at the top of this component re-fetches the
              // DocumentReference + Provenance. Without this the viewer keeps
              // rendering the stale `docStatus: 'preliminary'` chrome (Edit /
              // Lock Note buttons + countdown card) until the user manually
              // reloads. Also clear the Medplum SDK search cache and emit
              // `progressNotes:refresh` so the left-rail `<ListLayout>` re-runs
              // its `medplum.search('DocumentReference', …)` fetch — without
              // this the rail row keeps showing its amber "Draft" pill against
              // the now-locked note. Mirrors `ProgressNoteEdit.tsx:1057-1059`.
              onLockSuccess={() => {
                medplum.invalidateSearches('DocumentReference')
                medplum.invalidateSearches('Provenance')
                emitPanelEvent('progressNotes:refresh')
                setComponentKey(v4())
              }}
              patientId={patientId}
            />
          </div>
        )}

        {/*
         * <ProgressNoteEdit> panel — driven by Documents.tsx's local
         * `showEdit` state which the viewer toggles via `onToggleEdit`.
         * The panel renders its own Cancel control; we just need to flip
         * `showEdit` back when the user submits.
         */}
        {isProgressNote(resource) &&
          hasQuestionnaireResponseTarget(provenance) &&
          !isManager &&
          showEdit && (
            <div className="mt-4">
              <ProgressNoteEdit
                onSave={() => {
                  setComponentKey(v4())
                  setShowEdit(false)
                  setAutoApplyAiDraft(false)
                }}
                onCancel={() => {
                  setShowEdit(false)
                  setAutoApplyAiDraft(false)
                }}
                documentReference={resource}
                provenance={provenance}
                // PD-1647 — the charge quote resolves the rendering provider from
                // the Encounter; without it the editor falls back to the caller and
                // an assisting nurse re-tiers the treating physician's line. Every
                // other use of this prop is create-mode only, so edit mode is
                // unaffected: submit, autosave and the AI menu all resolve the
                // encounter from the Provenance.
                encounterId={encounterId}
                autoApplyAiDraft={autoApplyAiDraft}
              />
            </div>
          )}

        {isCareCoordinatorNote(resource) && patient && (
          <div className="mt-4">
            <Button color={showEdit ? 'red' : 'blue'} onClick={() => setShowEdit(!showEdit)}>
              {showEdit ? 'Cancel' : 'Edit'}
            </Button>
            {showEdit && (
              <CareCoordinatorNote
                patient={patient}
                documentReference={resource}
                onSubmitted={() => {
                  setComponentKey(v4())
                  setShowEdit(false)
                }}
                onCancel={() => setShowEdit(false)}
              />
            )}
          </div>
        )}

        {/* Legacy "For physician's record only" billing block (rendered from
            the Claim items). Hidden for structured Progress Notes because the
            same billing data is now surfaced inside the note itself via
            `<BillingSummary>` at the tail of the Plan section. */}
        {!hideCcdaTables && !!claim && !!claim.item?.length && (
          <div className="mt-6 rounded-xl bg-[#F3F5F8] p-5">
            <p className="text-md mb-4 font-medium">For physician&apos;s record only:</p>
            <div className="flex flex-col gap-2">
              {claim.item.map((item, index) => {
                const sequence = item.sequence ?? index + 1
                const quantity = item.quantity?.value
                const unitPrice = item.unitPrice?.value
                const diagnosisCodes = getDiagnosisCodes(item)
                const billPrefix = index === 0 ? 'Bill ' : ''

                return (
                  <div key={sequence} className="flex items-center gap-3 text-sm">
                    <span className="w-8 text-right text-[#7D7987]">{sequence}</span>
                    <span className="text-[#7D7987]">{billPrefix}</span>
                    <span className="font-mono">{getProcedureDisplay(item)}:</span>
                    <span className="font-mono">
                      [{diagnosisCodes === '-' ? '' : diagnosisCodes}]
                    </span>
                    {quantity !== undefined && unitPrice !== undefined && (
                      <span className="ml-2">
                        {quantity} x ${formatMoney(unitPrice)}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>

            <div className="mt-4 flex flex-col gap-2 text-base">
              <div className="flex items-start gap-4">
                <p className="w-[140px] text-sm font-bold text-[#7D7987]">Billing Status</p>
                <p>
                  {claim.created ? `Created on ${formatDateTime(claim.created, '-', true)}` : '-'}
                  {claim.status ? `, ${claim.status}` : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {!!ccdaTables.length && !hideCcdaTables && (
          // PD-1159: ToC scrolls with the content — must not be sticky.
          <div>
            <div className="my-4 h-[1px] bg-gray-200" />

            <p className="mb-2 text-sm font-bold text-[#7D7987]">Table of Contents</p>
            <div className="mb-4 flex flex-wrap gap-5 pb-1 text-sm font-medium text-[#3C6ADD]">
              {ccdaTables.map((panel) => (
                <button
                  key={panel.title}
                  className="hover:underline focus:underline"
                  onClick={() => scrollToPanel(panel.title)}
                  type="button"
                >
                  {panel.title}
                </button>
              ))}
            </div>
            <div className="my-4 h-[1px] bg-gray-200" />
          </div>
        )}

        {!hideCcdaTables &&
          ccdaTables.map((table) => (
            <div
              key={table.title}
              className="mt-10"
              ref={(el: HTMLDivElement | null) => {
                if (table.title) {
                  panelRefs.current[table.title] = el
                }
              }}
            >
              <p className="text-xl">{table.title}</p>

              <div
                className="ccda-content"
                dangerouslySetInnerHTML={{ __html: table.textContent }}
              />
            </div>
          ))}

        {!hideCcdaTables &&
          textHtml.map((html) => (
            <div
              key={html.slice(0, 40)}
              className="ccda-content mt-10"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ))}

        {/* PD-1897 — a structured note closes with the signature inside the viewer;
          a CCDA-rendered one closes with it here, below its body. */}
        {noteRenderMode === 'document-fallback' && noteSignature && (
          <div className="mt-10">
            <ElectronicSignature signature={noteSignature} />
          </div>
        )}

        {(!!images.length || !!tifImages.length) && (
          <div className="mt-10">
            <p className="mb-2 text-xl">Images</p>
            <div className="flex flex-wrap gap-5">
              {(images || []).map((image) => (
                <div key={image} className="h-[600px] w-[600px]">
                  <img src={image} alt={image} className="border-4 border-white drop-shadow-lg" />
                </div>
              ))}
              {(tifImages || []).map((image) => (
                <div key={image} className="h-[600px] w-[600px]">
                  <img src={image} alt={image} className="border-4 border-white drop-shadow-lg" />
                </div>
              ))}
            </div>
          </div>
        )}

        {!!pdfs.length && (
          <div className="mt-10">
            <p className="mb-2 text-xl">Attachments</p>
            {pdfs.map((pdf, index) => (
              <div key={`${pdf.url}-${index}`} className="mb-8">
                <PdfViewer fileUrl={pdf.url} />
              </div>
            ))}
          </div>
        )}

        {restAttachmentUrls.map((attachment) => (
          <div className="mt-4" key={attachment.url}>
            <a
              href={attachment.url}
              download={attachment.title}
              rel="noreferrer"
              className="text-primary flex gap-2 hover:opacity-80"
              // S14 — attachment downloads feed the audit trail too.
              onClick={() => resource?.id && reportAuditDownload(resource.id)}
            >
              <IconDownload width={20} /> {attachment.title}
            </a>
          </div>
        ))}
      </div>

      {resource?.id && patientId && patient && (
        <ReviewFollowupModal
          opened={isFollowupModalOpen}
          onClose={() => setIsFollowupModalOpen(false)}
          onSaved={handleFollowupSaved}
          isAlreadySaved={isFollowupSaved}
          documentReferenceId={resource.id}
          patientId={patientId}
          patientName={
            patient?.name?.[0] ? formatHumanName(patient.name[0]) : resource.subject?.display
          }
          patient={patient}
          providerName={getProvider(provenance)}
          sourceName={getSource(provenance)}
          providerReference={getProviderReference(provenance)}
          sourceReference={getSourceReference(provenance)}
        />
      )}
    </div>
  )
}
